import {
  BehaviorSubject,
  forkJoin as observableForkJoin,
  Observable,
  of as observableOf,
  of,
} from 'rxjs';

import {catchError, distinctUntilChanged, first, map, shareReplay, takeWhile} from 'rxjs/operators';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {Resource, ResourceWithPermissionHints} from '../resource';
import {CacheService} from '../../cache/cache.service';
import {ResourceService} from '../resource.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DialogsService} from '../../shared/dialog/dialogs.service';
import {LanguageService} from '../../language/language.service';
import {DetailService} from './detail.service';
import {ResourceDetailAbstractComponent, ValidationInfo} from './detail-abstract.component';
import {ViewTabComponent} from './viewtab.component';
import {ErrorService} from '../../error/error.service';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {AddViewEvent} from '../events/addviewevent';
import {DomUtilsService} from '../../utils/dom-utils.service';
import {MatTabGroup} from '@angular/material/tabs';

@Component({
  selector: 'resource-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({transform: 'translateX(100%)', opacity: 0}),
        animate('200ms', style({transform: 'translateX(0)', opacity: 1})),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResourceDetailComponent
  extends ResourceDetailAbstractComponent
  implements AfterViewInit, AfterViewChecked
{
  loading: boolean = true;

  ngAfterViewChecked() {
    if (this.saveAllTabsOnInit) this.saveAllTabs();
  }

  saveAllTabsOnInit = false;
  @Input()
  set startEdit(value: boolean) {
    if (value === true) {
      this.editClick();
    }
  }

  @Input()
  set setResource(res: BehaviorSubject<ResourceWithPermissionHints>) {
    if (res != null) {
      this.resource = res;
      this.viewConfig = this.detailService
        .getBodyTabConfigOfResourceType(
          this.resource.asObservable().pipe(
            map((res: ResourceWithPermissionHints) => {
              if (
                res[this.envConfig.getAttributeName('ObjectType')] != null &&
                res[this.envConfig.getAttributeName('ObjectType')].Values != null
              ) {
                return res[this.envConfig.getAttributeName('ObjectType')].Values;
              }
              return null;
            }),
            distinctUntilChanged(),
            shareReplay(1),
          ),
          this.resource.asObservable().pipe(shareReplay(1)),
          this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1)),
        )
        .pipe(
          catchError(error => {
            this.cacheService
              .getLanguageResource()
              .pipe(first())
              .subscribe(lr => {
                const title = LanguageService.translate(
                  'ResourceDetailErrorParsingTitle',
                  'Error Parsing Dialog Configuration',
                  lr,
                );
                const msg = LanguageService.translate(
                  'ResourceDetailErrorParsingMessage',
                  'There occurred an error while parsing the dialog configuration',
                  lr,
                );
                setTimeout(() => {
                  this.dialogsService.webserviceError(error, title, msg, lr).subscribe(
                    () => {
                      //this.afterDelete.emit(true);
                    },
                    () => {},
                  );
                });
              });

            console.error(error);
            return observableOf(null);
          }),
        );

      this.changeDetector.detectChanges();
      this.loading = false;
      this.viewConfig.pipe(takeWhile(() => this.alive)).subscribe(() => {
        this.changeDetector.markForCheck();
        this.onResize();
      });

      this.resource
        .pipe(
          catchError((error, caught) => this.handleFatalError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((res: ResourceWithPermissionHints) => {
          if (
            (res != null &&
              res[this.envConfig.getAttributeName('ObjectID')] != null &&
              this._lastResourceID != res[this.envConfig.getAttributeName('ObjectID')].Values) ||
            this._lastResourceID == null
          ) {
            this._lastResourceID = res[this.envConfig.getAttributeName('ObjectID')].Values;
            this.saveCopy = new Resource();

            this.editAllowed = false;
            this.deleteAllowed = false;

            Object.keys(res).forEach((key: string) => {
              if (Array.isArray(res[key].Values)) {
                this.saveCopy[key] = res[key].Values.slice();
              } else {
                this.saveCopy[key] = res[key].Values;
              }
              if (res[key].PermissionHint) {
                // added because there are null values without PermissionHint
                if (
                  !this.editAllowed &&
                  (res[key].PermissionHint.indexOf('Modify') !== -1 ||
                    res[key].PermissionHint.indexOf('Add') !== -1 ||
                    res[key].PermissionHint.indexOf('Remove') !== -1)
                ) {
                  this.editAllowed = true;
                }
                if (!this.deleteAllowed && res[key].PermissionHint.indexOf('Delete') !== -1) {
                  this.deleteAllowed = true;
                }
              }
            });

            this.freshResource = true;
          }
        });
    }
  }

  @Output() afterDelete: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('detailtabs', {static: true}) tabContainer: ElementRef;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  @ViewChildren(ViewTabComponent) viewTabs: QueryList<ViewTabComponent>;

  private _lastResourceID: string = null;

  selectedTab: number = 0;

  editAllowed: boolean = true;
  deleteAllowed: boolean = true;

  constructor(
    cacheService: CacheService,
    resourceService: ResourceService,
    dialog: MatDialog,
    snackBar: MatSnackBar,
    dialogsService: DialogsService,
    elementRef: ElementRef,
    private detailService: DetailService,
    changeDetector: ChangeDetectorRef,
    errorService: ErrorService,
    private domUtils: DomUtilsService,
    protected envConfig: EnvironmentConfigService,
  ) {
    super(
      cacheService,
      snackBar,
      changeDetector,
      dialog,
      dialogsService,
      resourceService,
      elementRef,
      errorService,
      envConfig,
    );
  }

  onTabSelectedChange(event): void {
    if (this.selectedTab !== event.index) {
      this.selectedTab = event.index;
      if (this.viewTabs != null && this.viewTabs.length > 0) {
        this.viewTabs.toArray()[this.selectedTab]?.refreshUI();
      }

      // Scroll previous/next tab into view area of tab list

      const tabHeader = this.tabGroup._tabHeader;
      const tabHeaderItemsArray = tabHeader._items.toArray();
      const selectedLabelEl = tabHeaderItemsArray[this.selectedTab].elementRef.nativeElement;

      const currentPositions = {
        tabStart: selectedLabelEl.offsetLeft,
        tabEnd: selectedLabelEl.offsetLeft + selectedLabelEl.offsetWidth,
        tabListStart: tabHeader.scrollDistance,
        tabListEnd:
          tabHeader.scrollDistance + tabHeader._tabListContainer.nativeElement.offsetWidth,
      };

      if (
        currentPositions.tabEnd + 5 > currentPositions.tabListEnd &&
        this.viewTabs.length - 1 > this.selectedTab
      ) {
        const nextLabel = tabHeaderItemsArray[this.selectedTab + 1];
        tabHeader.scrollDistance += nextLabel.elementRef.nativeElement.offsetWidth / 2;
      } else if (
        currentPositions.tabStart - 5 < currentPositions.tabListStart &&
        this.selectedTab > 0
      ) {
        const prevLabel = tabHeaderItemsArray[this.selectedTab - 1];
        tabHeader.scrollDistance -= prevLabel.elementRef.nativeElement.offsetWidth / 2;
      }
    }
  }

  onResize() {
    super.onResize();
    if (this.viewTabs != null && this.viewTabs.length > 0) {
      this.viewTabs.toArray()[this.selectedTab]?.refreshUI();
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  private editClick(): void {
    if (!this.editAllowed) return;
    this.editEnabled = true;
  }

  public getValidationErrorCount(includeNonTouched: boolean = false): Observable<number> {
    const labels = this.getClearTabLabels();
    if (this.viewTabs != null) {
      let tabObs: Observable<ValidationInfo>[] = [];
      this.viewTabs.forEach((tab, index) => {
        tabObs.push(
          tab
            .getErrorCount(includeNonTouched)
            .pipe(map(number => new ValidationInfo(index, number))),
        );
      });
      return observableForkJoin(tabObs).pipe(
        map((valids: ValidationInfo[]) => {
          let errors: number = 0;
          valids.forEach(valid => {
            errors += valid.Errors;
            if (valid.Errors > 0 && labels[valid.TabIndex]) {
              const labelContainer = labels[valid.TabIndex].querySelector('.label-container');
              if (labelContainer) {
                labelContainer.classList.add(this.tabLabelErrorClass);
              }
            }
          });
          return errors;
        }),
      );
    } else {
      return observableOf(0);
    }
  }

  protected saveAllTabs(): void {
    if (this.viewTabs == null || this.viewTabs.length === 0) {
      this.saveAllTabsOnInit = true;
    } else {
      this.saveAllTabsOnInit = false;
      this.viewTabs.forEach(vt => {
        vt.saveAllControlValues();
      });
    }
  }

  private readonly tabLabelErrorClass: string = 'tab-with-errors';

  private getClearTabLabels(): HTMLElement[] {
    const labels = Array.from(
      this.tabContainer.nativeElement.querySelectorAll('.mdc-tab__text-label'),
    ) as HTMLElement[];

    labels.forEach(label => {
      const labelContainers = label.querySelectorAll('.label-container');
      labelContainers.forEach(container => {
        container.classList.remove(this.tabLabelErrorClass);
      });
    });

    return labels;
  }

  private cancelClick(): void {
    this.resetComponent();
  }

  protected resetComponent(): void {
    this.editEnabled = false;
    this.freshResource = true;
    this.getClearTabLabels();
    this.viewTabs.forEach((vt: ViewTabComponent) => {
      vt.markAllControlsAsNotTouched();
    });
    this.resourceService
      .getResourceWithPermissionHints(this._lastResourceID)
      .pipe(
        catchError((error, caught) => of(null)),
        first(),
      )
      .subscribe((newResource: ResourceWithPermissionHints) => {
        // no error handling - because there could be an 404 if resource was deleted.
        if (newResource) {
          this.freshResource = true;
          this.resourceLocaleChange = {};
          this.resource.next(newResource);
          const locale = this.resLocale.getValue();
          this.resLocale.next('');
          this.resLocale.next(locale);
        }
      });
  }

  private deleteClick(): void {
    if (!this.deleteAllowed) return;
    const resource: ResourceWithPermissionHints = this.resource.getValue();

    if (resource == null || resource[this.envConfig.getAttributeName('ObjectID')] == null) return;

    let desc: string = resource[this.envConfig.getAttributeName('ObjectID')].Values;
    if (
      resource[this.envConfig.getAttributeName('DisplayName')] != null &&
      resource[this.envConfig.getAttributeName('DisplayName')].Values != null &&
      resource[this.envConfig.getAttributeName('DisplayName')].Values.length > 0
    ) {
      desc = resource[this.envConfig.getAttributeName('DisplayName')].Values;
    }

    this.cacheService
      .getLanguageResource()
      .pipe(first())
      .subscribe(lr => {
        const message: string = LanguageService.translate(
          'ResourceDetailDeleteConfirmMessage',
          'Do you really want to delete {0}?',
          lr,
          [desc],
        );
        const title: string = LanguageService.translate(
          'ResourceDetailDeleteConfirmTitle',
          'Delete {0}',
          lr,
          [desc],
        );
        const yes: string = LanguageService.translate('Yes', 'Yes', lr);
        const no: string = LanguageService.translate('No', 'No', lr);

        this.dialogsService
          .confirm(title, message, yes, no)
          .pipe(first())
          .subscribe((result: any) => {
            if (result === true) {
              this.resourceService
                .deleteResource(resource[this.envConfig.getAttributeName('ObjectID')].Values)
                .subscribe(
                  (ok: boolean) => {
                    // TODO do we enter this path only when the request was successful?
                    const success: string = LanguageService.translate(
                      'ResourceDetailDeleteSuccess',
                      'Resource {0} deleted successfully.',
                      lr,
                      [desc],
                    );
                    this.snackBar.open(success, undefined, {duration: 2000});
                    this.afterDelete.emit(true);
                  },
                  (error: any) => {
                    let title: string = LanguageService.translate(
                      'ResourceDetailDeleteFailureTitle',
                      'Deleting resource failed',
                      lr,
                    );
                    let message: string = LanguageService.translate(
                      'ResourceDetailDeleteFailureMessage',
                      'An error occurred by deleting {0}.',
                      lr,
                      [desc],
                    );

                    this.dialogsService.webserviceError(error, title, message, lr);
                  },
                );
            }
          });
      });
  }

  toggleAdvancedDetail(): void {
    super.toggleAdvancedDetail();

    if (this.advancedDetail) {
      this.viewConfig = this.detailService
        .getAdvancedBodyTabConfigOfResourceType(
          this.resource.asObservable().pipe(
            map((res: ResourceWithPermissionHints) => {
              if (
                res[this.envConfig.getAttributeName('ObjectType')] != null &&
                res[this.envConfig.getAttributeName('ObjectType')].Values != null
              ) {
                return res[this.envConfig.getAttributeName('ObjectType')].Values;
              }
              return null;
            }),
            distinctUntilChanged(),
            shareReplay(1),
          ),
          this.resource.asObservable().pipe(shareReplay(1)),
          this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1)),
        )
        .pipe(
          catchError(error => {
            this.cacheService
              .getLanguageResource()
              .pipe(first())
              .subscribe(lr => {
                const title = LanguageService.translate(
                  'ResourceDetailErrorParsingTitle',
                  'Error Parsing Dialog Configuration',
                  lr,
                );
                const msg = LanguageService.translate(
                  'ResourceDetailErrorParsingMessage',
                  'There occurred an error while parsing the dialog configuration',
                  lr,
                );
                setTimeout(() => {
                  this.dialogsService.webserviceError(error, title, msg, lr).subscribe(
                    () => {
                      this.afterDelete.emit(true);
                    },
                    () => {},
                  );
                });
              });

            console.error(error);
            return observableOf(null);
          }),
        );
      this.viewConfig.pipe(takeWhile(() => this.alive)).subscribe(() => {
        this.changeDetector.markForCheck();
        this.onResize();
      });
    } else {
      this.viewConfig = this.detailService
        .getBodyTabConfigOfResourceType(
          this.resource.asObservable().pipe(
            map((res: ResourceWithPermissionHints) => {
              if (
                res[this.envConfig.getAttributeName('ObjectType')] != null &&
                res[this.envConfig.getAttributeName('ObjectType')].Values != null
              ) {
                return res[this.envConfig.getAttributeName('ObjectType')].Values;
              }
              return null;
            }),
            distinctUntilChanged(),
            shareReplay(1),
          ),
          this.resource.asObservable().pipe(shareReplay(1)),
          this.resLocale.asObservable().pipe(distinctUntilChanged(), shareReplay(1)),
        )
        .pipe(
          catchError(error => {
            this.cacheService
              .getLanguageResource()
              .pipe(first())
              .subscribe(lr => {
                const title = LanguageService.translate(
                  'ResourceDetailErrorParsingTitle',
                  'Error Parsing Dialog Configuration',
                  lr,
                );
                const msg = LanguageService.translate(
                  'ResourceDetailErrorParsingMessage',
                  'There occurred an error while parsing the dialog configuration',
                  lr,
                );
                setTimeout(() => {
                  this.dialogsService.webserviceError(error, title, msg, lr).subscribe(
                    () => {
                      this.afterDelete.emit(true);
                    },
                    () => {},
                  );
                });
              });

            console.error(error);
            return observableOf(null);
          }),
        );
      this.viewConfig.pipe(takeWhile(() => this.alive)).subscribe(() => {
        this.changeDetector.markForCheck();
        this.onResize();
      });
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (!this.freshResource) {
      $event.returnValue = false;
    }
  }
}
